/**
 * Local-first offline store for presets.
 *
 * - Every successful network read mirrors the response to localStorage.
 * - Every write is applied optimistically to the local mirror first,
 *   then attempted over the network.  Failed writes are queued for
 *   replay when connectivity returns.
 *
 * The app UI reads from this store, so the whole thing works with no
 * network (browse / add / edit / delete / share) and reconciles when
 * a connection is available.
 */

const PRESETS_KEY = "sydpid.presets.v1";
const QUEUE_KEY = "sydpid.queue.v1";

function safeJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const val = JSON.parse(raw);
    return val ?? fallback;
  } catch {
    return fallback;
  }
}

export function getCachedPresets() {
  return safeJSON(PRESETS_KEY, []);
}

export function cachePresets(list) {
  try {
    localStorage.setItem(PRESETS_KEY, JSON.stringify(list || []));
  } catch { /* quota exceeded */ }
}

export function upsertLocal(preset) {
  const list = getCachedPresets();
  const idx = list.findIndex((p) => p.id === preset.id);
  if (idx >= 0) list[idx] = preset;
  else list.unshift(preset);
  cachePresets(list);
}

export function removeLocal(id) {
  cachePresets(getCachedPresets().filter((p) => p.id !== id));
}

export function replaceLocalId(oldId, newPreset) {
  const list = getCachedPresets().filter((p) => p.id !== oldId);
  list.unshift(newPreset);
  cachePresets(list);
}

// ---- queue ----------------------------------------------------------------

export function getQueue() {
  return safeJSON(QUEUE_KEY, []);
}

function writeQueue(q) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q || []));
  } catch { /* ignore */ }
}

export function enqueue(op) {
  const q = getQueue();
  q.push({ ...op, at: Date.now() });
  writeQueue(q);
}

export function popFront() {
  const q = getQueue();
  const first = q.shift();
  writeQueue(q);
  return first;
}

export function queueSize() {
  return getQueue().length;
}

export function clearQueue() {
  writeQueue([]);
}

// ---- helpers --------------------------------------------------------------

export function newLocalId() {
  const rnd =
    (globalThis.crypto && crypto.randomUUID && crypto.randomUUID()) ||
    Math.random().toString(36).slice(2) + Date.now().toString(36);
  return `local-${rnd}`;
}

export function isLocalId(id) {
  return typeof id === "string" && id.startsWith("local-");
}

export function nowIso() {
  return new Date().toISOString();
}
